// Costos adicionales (PLAN-NOCHE §2b, criterio 3): lo que encuentra el técnico en la visita o lo que pide el caso —
// viáticos, mufa, anclaje o estructura especial, estructura sobre suelo, mover equipos, obra civil, otro— con un botón
// rápido del catálogo de Diego y el MONTO que paga el cliente. Antes o después de generar: cada cambio crea una versión
// nueva y, si ya hay PDF, lo regenera (usarRegenerarPdf). El vendedor captura los costos (los técnicos ya no entran,
// ADENDA A0b) y NO ve el costo para Solarix (11-oct: solo Diego): el servidor lo pone del catálogo.
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { ADICIONALES_DE_ESTRUCTURA, NOMBRE_CORTO_ESTRUCTURA, NOTA_COSTO_MAX, conIva, estructuraDe, fechaHoraCorta } from '@cotizador/compartido';
import type { CostoAdicional, CuerpoCosto, ItemCatalogoVista } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { usarUsuario } from '../../sesion.tsx';
import { navegar, usarRuta } from '../../ruteo.tsx';
import { Boton, BotonConfirmar, CampoArea, CampoTexto, Chip, ErrorEnLinea, Interruptor, Seccion, Vacio } from '../../componentes/ui.tsx';
import { dinero } from '../../logica/etiquetas.ts';
import { numeroDe } from '../../logica/cuestionario.ts';
import { usarVistaPrecios } from '../../logica/vista-precios.ts';
import { usarCotizacion, usarPuedeAqui, usarRegenerarPdf } from './contexto.tsx';
import type { ResultadoRegenerar } from './contexto.tsx';

const ORIGEN: Record<CostoAdicional['origen'], string> = { visita_tecnica: 'Visita técnica', vendedor: 'Vendedor', ia: 'IA' };
const UNIDAD: Record<ItemCatalogoVista['unidad'], string> = { proyecto: 'por proyecto', pieza: 'por pieza', panel: 'por panel', kwp: 'por kWp', metro: 'por metro', maniobra: 'por maniobra' };
/** Si el catálogo de la tabla todavía no los trae (tabla vieja), igual se ofrecen como concepto (monto a mano). */
const RAPIDOS_SIN_CATALOGO = ['Viáticos fuera de Hermosillo', 'Modificación de mufa', 'Anclaje especial', 'Estructura especial', 'Estructura sobre suelo', 'Mover equipos del techo', 'Obra civil'];

function textoRegenerado(r: ResultadoRegenerar): string | null {
  if (r.tipo === 'regenerado') return `Listo: se generó la versión ${r.numero} del PDF con este cambio.`;
  if (r.tipo === 'pendiente') return `El PDF anterior ya no dice lo último y no se regeneró solo: ${r.motivo}`;
  return null;
}

function Costo({ c, alCambiar }: { c: CostoAdicional; alCambiar: (texto: string | null) => void }) {
  const { ejecutar, cot } = usarCotizacion();
  const { veCostos } = usarUsuario();
  const puede = usarPuedeAqui();
  const regenerar = usarRegenerarPdf();
  const [quitando, setQuitando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const iva = cot.borrador.parametros.iva_pct;
  const total = c.cantidad * c.precio_cliente_sin_iva;
  async function quitar() {
    setQuitando(true);
    setError(null);
    try {
      await ejecutar((x) => api.quitarCosto(x.id, c.id, x.revision));
      alCambiar(textoRegenerado(await regenerar(`Quitó «${c.concepto}»`)));
    } catch (e) {
      setError(e);
      setQuitando(false);
    }
  }
  return (
    <li className="costo">
      <div className="costo__arriba">
        <div>
          <p className="costo__concepto">
            {c.concepto}
            {c.cantidad > 1 && <span className="costo__cantidad"> × {c.cantidad}</span>}
          </p>
          <p className="costo__meta">
            {ORIGEN[c.origen]} · reportó {c.reportado_por} · {fechaHoraCorta(c.capturado_en)}
          </p>
        </div>
        <div className="costo__monto">
          <p className="costo__total">{dinero(total, false)}</p>
          <p className="costo__iva">+ IVA · {dinero(conIva(total, iva), false)} con IVA</p>
        </div>
      </div>
      <div className="costo__chips">
        {/* El costo para Solarix: solo Diego (el servidor no se lo manda a nadie más). */}
        {veCostos &&
          (c.costo_solarix_sin_iva === null ? (
            <Chip tono="ambar">Costo para Solarix sin confirmar</Chip>
          ) : (
            <Chip tono={total - c.cantidad * c.costo_solarix_sin_iva < 0 ? 'rojo' : 'neutro'}>Le cuesta a Solarix {dinero(c.cantidad * c.costo_solarix_sin_iva, false)}</Chip>
          ))}
        {c.aplica_descuento_contado ? <Chip tono="prusia">Lleva el 15 % de contado</Chip> : <Chip tono="neutro">Precio de contado</Chip>}
      </div>
      {c.nota && <p className="costo__nota">{c.nota}</p>}
      {puede('capturar_costos') && (
        <div className="costo__acciones">
          <BotonConfirmar pregunta="¿Quitar este costo?" cargando={quitando} icono={<Trash2 aria-hidden />} etiquetaAccesible={`Quitar ${c.concepto}`} alConfirmar={() => void quitar()}>
            Quitar
          </BotonConfirmar>
        </div>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </li>
  );
}

/** Cantidad que sugiere la unidad del catálogo (por panel o por kWp, con la opción recomendada). */
function cantidadSugerida(item: ItemCatalogoVista | null, paneles: number | null, kwp: number | null): string {
  if (item?.unidad === 'panel' && paneles) return String(paneles);
  if (item?.unidad === 'kwp' && kwp) return String(Math.max(1, Math.round(kwp)));
  return '1';
}

function FormaCosto({ inicial, alTerminar }: { inicial: ItemCatalogoVista | string | null; alTerminar: (texto: string | null) => void }) {
  const { ejecutar, cot } = usarCotizacion();
  const { usuario, veCostos } = usarUsuario();
  const regenerar = usarRegenerarPdf();
  const r = cot.borrador.resultado;
  const rec = r?.opciones.find((o) => o.id === r.recomendada) ?? null;
  const item = inicial && typeof inicial === 'object' ? inicial : null;
  const miNombre = usuario.nombre.split(' ')[0] ?? '';
  const [concepto, setConcepto] = useState(item ? item.concepto : typeof inicial === 'string' ? inicial : '');
  const [cantidad, setCantidad] = useState(cantidadSugerida(item, rec?.paneles ?? null, rec?.kwp ?? null));
  const [monto, setMonto] = useState(item?.precio_contado_sugerido ? String(item.precio_contado_sugerido) : '');
  const [costo, setCosto] = useState('');
  const [reporto, setReporto] = useState(miNombre);
  const [descuento, setDescuento] = useState(false);
  const [nota, setNota] = useState('');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ayudaMonto = item?.precio_contado_sugerido
    ? `Sugerido ${dinero(item.precio_contado_sugerido)} ${UNIDAD[item.unidad]}; lo puedes cambiar.`
    : item?.rango_precio_contado
      ? `Entre ${dinero(item.rango_precio_contado[0])} y ${dinero(item.rango_precio_contado[1])} ${UNIDAD[item.unidad]}, según el técnico.`
      : 'Lo que paga el cliente, sin IVA, por unidad.';

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const e: Record<string, string> = {};
    const n = numeroDe(cantidad);
    const p = numeroDe(monto);
    const k = veCostos ? numeroDe(costo) : null;
    if (concepto.trim().length < 2) e.concepto = 'Escribe qué es (mínimo 2 letras).';
    if (n === null || !Number.isInteger(n) || n < 1) e.cantidad = 'Cantidad entera, 1 o más.';
    if (p === null || !Number.isFinite(p) || p <= 0) e.monto = 'Escribe cuánto paga el cliente (sin IVA).';
    if (k !== null && (!Number.isFinite(k) || k < 0)) e.costo = 'Número no válido.';
    if (reporto.trim().length < 2) e.reporto = '¿Quién lo reportó? (tú, Eduardo, Víctor…)';
    if (nota.trim().length > NOTA_COSTO_MAX) e.nota = `La nota pasa de ${NOTA_COSTO_MAX} letras: acórtala.`;
    setErrores(e);
    if (Object.keys(e).length) return;
    const cuerpo: CuerpoCosto = {
      concepto: concepto.trim(),
      cantidad: n!,
      precio_cliente_sin_iva: p!,
      costo_solarix_sin_iva: k,
      aplica_descuento_contado: descuento,
      reportado_por: reporto.trim(),
      origen: reporto.trim().toLowerCase() === miNombre.toLowerCase() ? 'vendedor' : 'visita_tecnica',
      nota: nota.trim() || null,
      ...(item ? { catalogo_id: item.id } : {}),
    };
    setGuardando(true);
    setError(null);
    try {
      await ejecutar((c) => api.agregarCosto(c.id, c.revision, cuerpo));
      alTerminar(textoRegenerado(await regenerar(`Agregó «${cuerpo.concepto}» (${dinero(cuerpo.precio_cliente_sin_iva * cuerpo.cantidad)} sin IVA)`)));
    } catch (x) {
      setError(x);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form className="hoja pila" onSubmit={(e) => void guardar(e)} noValidate aria-label="Agregar costo adicional">
      <CampoTexto etiqueta="Concepto" maxLength={120} value={concepto} onChange={(e) => setConcepto(e.target.value)} error={errores.concepto} />
      <div className="rejilla-2">
        <CampoTexto etiqueta="Cantidad" inputMode="numeric" value={cantidad} onChange={(e) => setCantidad(e.target.value)} error={errores.cantidad} ayuda={item ? UNIDAD[item.unidad] : undefined} />
        <CampoTexto etiqueta="Monto que paga el cliente" ayuda={ayudaMonto} inputMode="decimal" value={monto} onChange={(e) => setMonto(e.target.value)} error={errores.monto} />
      </div>
      {veCostos && (
        <CampoTexto
          etiqueta="Costo para Solarix (solo lo ves tú)"
          ayuda={item ? 'Vacío = el del catálogo (lo pone el servidor).' : 'Por unidad, sin IVA. Vacío si no se sabe.'}
          inputMode="decimal"
          value={costo}
          onChange={(e) => setCosto(e.target.value)}
          error={errores.costo}
        />
      )}
      <CampoTexto etiqueta="¿Quién lo reportó?" maxLength={60} value={reporto} onChange={(e) => setReporto(e.target.value)} error={errores.reporto} ayuda="Tú, o el técnico que fue a la visita (Eduardo, Víctor)." />
      <Interruptor
        etiqueta="Le aplica el 15 % de contado"
        detalle={descuento ? 'El monto es precio de LISTA: de contado se le quita el 15 %.' : 'El monto es lo que paga DE CONTADO (sin IVA); en meses se cobra la lista.'}
        marcado={descuento}
        alCambiar={setDescuento}
      />
      <CampoArea
        etiqueta="Nota (opcional)"
        rows={2}
        maxLength={NOTA_COSTO_MAX}
        value={nota}
        onChange={(e) => setNota(e.target.value)}
        error={errores.nota}
        // verificación del 30-oct-2026: al llegar al tope, lo que se pega o se teclea de más NO entra (maxLength) y antes no se decía
        ayuda={nota.length >= NOTA_COSTO_MAX ? `${NOTA_COSTO_MAX} de ${NOTA_COSTO_MAX} letras: llegaste al tope; lo que escribas de más no entra.` : nota.length ? `${nota.length} de ${NOTA_COSTO_MAX} letras.` : `Hasta ${NOTA_COSTO_MAX} letras.`}
        placeholder="Ej. la mufa está oxidada; CFE no la acepta."
      />
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      <div className="fila-botones fila-botones--fin">
        <Boton variante="fantasma" onClick={() => alTerminar(null)}>
          Cancelar
        </Boton>
        <Boton type="submit" variante="principal" cargando={guardando} icono={<Plus aria-hidden />}>
          Agregar
        </Boton>
      </div>
    </form>
  );
}

export function SeccionCostos() {
  const { cot } = usarCotizacion();
  const puede = usarPuedeAqui();
  const { vista, error: errorVista } = usarVistaPrecios();
  const { consulta } = usarRuta();
  const costos = cot.borrador.costos_adicionales;
  const [forma, setForma] = useState<{ inicial: ItemCatalogoVista | string | null } | null>(null);
  // "Agregar el costo" desde "Lo que entendí de tus notas": abre el formulario con ese renglón del catálogo.
  const pedido = consulta.get('agregar');
  useEffect(() => {
    if (!pedido || !vista) return;
    const item = vista.catalogo.find((c) => c.id === pedido);
    setForma({ inicial: item ?? null });
    navegar(`/cotizaciones/${cot.id}/costos`, { reemplazar: true });
  }, [pedido, vista, cot.id]);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const total = costos.reduce((s, c) => s + c.cantidad * c.precio_cliente_sin_iva, 0);
  // Diego, 12-oct: con PTR, suelo o carport en el cuestionario la estructura ya va dentro del precio del sistema: el catálogo
  // que la cobra no se ofrece (sería doble cobro; el servidor tampoco lo deja y el motor lo marca en rojo).
  const estructura = estructuraDe(cot.borrador.cuestionario);
  const especial = estructura !== 'losa_lamina';
  const catalogo = (vista?.catalogo ?? []).filter((c) => !(especial && ADICIONALES_DE_ESTRUCTURA.includes(c.id)));
  // Hasta que llegue el catálogo no se pintan los botones (si no, cambian bajo el dedo cuando llega).
  const listo = vista !== null || errorVista !== null;
  const extras = listo
    ? RAPIDOS_SIN_CATALOGO.filter((r) => !(especial && /^Estructura/.test(r))).filter((r) => !(vista?.catalogo ?? []).some((c) => c.concepto.toLowerCase().startsWith(r.toLowerCase().slice(0, 10))))
    : [];
  const abrir = (inicial: ItemCatalogoVista | string | null) => {
    setMensaje(null);
    setForma({ inicial });
  };
  const terminar = (texto: string | null) => {
    setForma(null);
    setMensaje(texto);
  };
  return (
    <Seccion
      titulo="Costos adicionales"
      descripcion="Viáticos, mufa, anclaje o estructura especial… Toca uno y pon el monto que paga el cliente: se suma a las opciones con su IVA. Antes o después del PDF."
    >
      <div className="pila">
        {puede('capturar_costos') && !forma && (
          <div className="ideas ideas--catalogo" aria-label="Agregar rápido">
            {catalogo.map((c) => (
              <button key={c.id} type="button" className="pastilla" onClick={() => abrir(c.id === 'otro' ? null : c)}>
                <Plus aria-hidden /> {c.id === 'otro' ? 'Otro' : c.concepto.replace(/\s*\(.*\)$/, '')}
                {c.precio_contado_sugerido ? <span className="pastilla__monto"> {dinero(c.precio_contado_sugerido)}</span> : null}
              </button>
            ))}
            {extras.map((r) => (
              <button key={r} type="button" className="pastilla" onClick={() => abrir(r)}>
                <Plus aria-hidden /> {r}
              </button>
            ))}
            {listo && !catalogo.some((c) => c.id === 'otro') && (
              <button type="button" className="pastilla" onClick={() => abrir(null)}>
                <Plus aria-hidden /> Otro
              </button>
            )}
          </div>
        )}
        {especial && puede('capturar_costos') && !forma && (
          <p className="nota-suave">
            La estructura ({NOMBRE_CORTO_ESTRUCTURA[estructura]}) ya va dentro del precio del sistema: por eso aquí no aparecen «estructura elevada», «sobre suelo» ni «especial».
          </p>
        )}
        {forma && <FormaCosto key={forma.inicial && typeof forma.inicial === 'object' ? forma.inicial.id : String(forma.inicial)} inicial={forma.inicial} alTerminar={terminar} />}
        {mensaje && (
          <p className="nota-suave" role="status">
            {mensaje}
          </p>
        )}
        {costos.length === 0 && !forma ? (
          <Vacio titulo="Sin costos adicionales">
            {cot.etapa === 'precotizacion'
              ? 'Es precotización: la visita técnica puede pedir cambiar la mufa, estructura elevada o mover equipos. Se agregan aquí con un toque.'
              : 'Si la visita encontró algo que cobrar aparte, agrégalo aquí con un toque.'}
          </Vacio>
        ) : (
          costos.length > 0 && (
            <>
              <ul className="costos">
                {costos.map((c) => (
                  <Costo key={c.id} c={c} alCambiar={setMensaje} />
                ))}
              </ul>
              <p className="total-linea">
                Suman <strong>{dinero(total, false)}</strong> + IVA a cada opción.
              </p>
            </>
          )
        )}
        {/* Diego, junta del 13-oct (segunda tanda): el orden es Recibos → Revisión → Costos → Opciones. */}
        {cot.borrador.resultado && !forma && puede('editar_cotizaciones') && (
          <div className="fila-botones">
            <Boton variante="principal" icono={<ArrowRight aria-hidden />} onClick={() => navegar(`/cotizaciones/${cot.id}/opciones`)}>
              Seguir a Opciones
            </Boton>
          </div>
        )}
      </div>
    </Seccion>
  );
}
